import { useCallback, useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';
import { fetchWishlist, postFormData } from '../../../../modules/api';
import { useRecaptchaToken } from '../../../globalHooks/useRecaptchaToken';

const useWishList = () => {
  const favourites = useSelector((state) => state.favourites);

  const [wishlist, setWishlist] = useState({});

  const [form, setForm] = useState(null);

  const [intro, setIntro] = useState(null);

  const [waiting, setWaiting] = useState(true);

  const [showShareWithAFriend, setShowShareWithAFriend] = useState(false);

  const [showShareWithUs, setShowShareWithUs] = useState(false);

  const [currentFormAction, setCurrentFormAction] = useState(null);

  const flattenedFavourites = Object.keys(favourites).reduce(
    (previous, current, index) => [...previous, ...favourites[current]],
    []
  );

  const [shareWithAFriendInitial, setShareWithAFriendInitial] = useState({
    first_name: '',
    last_name: '',
    email: '',
    friend_first_name: '',
    friend_last_name: '',
    friend_email: '',
    opt_in: false,
    client_id: 'No client id detected',
  });

  const [shareWithUsInitial, setShareWithUsInitial] = useState({
    first_name: '',
    last_name: '',
    email: '',
    message: '',
    opt_in: false,
    client_id: 'No client id detected',
  });

  const shareWithAFriendValidationSchema = Yup.object({
    first_name: Yup.string().required('* required'),
    last_name: Yup.string().required('* required'),
    email: Yup.string()
      .email('Please enter a valid email address')
      .required('* required'),
    friend_first_name: Yup.string().required('* required'),
    friend_last_name: Yup.string().required('* required'),
    friend_email: Yup.string()
      .email('Please enter a valid email address')
      .required('* required'),
  });

  const shareWithUsValidationSchema = Yup.object({
    first_name: Yup.string().required('* required'),
    last_name: Yup.string().required('* required'),
    email: Yup.string()
      .email('Please enter a valid email address')
      .required('* required'),
    message: Yup.string().required('* required'),
  });

  const [cookies] = useCookies(['_ga']);
  const executeRecaptcha = useRecaptchaToken();

  // Get _ga client id
  const getGaClientId = () => {
    const gaCookie = cookies._ga;

    if (gaCookie) {
      const parts = gaCookie.split('.');
      if (parts.length === 4) {
        return `${parts[2]}.${parts[3]}`;
      }
    }

    return 'No client id detected';
  };

  // Set client id when _ga cookie is available
  useEffect(() => {
    const gaClientId = getGaClientId();

    setShareWithUsInitial((prevValues) => ({
      ...prevValues,
      client_id: gaClientId,
    }));

    setShareWithAFriendInitial((prevValues) => ({
      ...prevValues,
      client_id: gaClientId,
    }));
  }, [cookies._ga]);

  useEffect(() => {
    (async () => {
      try {
        setWaiting(true);

        const response = await fetchWishlist(flattenedFavourites);

        const { data: rawWishlist, form, intro } = response;

        // console.log(`Data: ${JSON.stringify(rawWishlist)}`);

        const wishlist = {
          holidays: Array.isArray(rawWishlist)
            ? rawWishlist.filter(({ type }) => type === 'holiday')
            : [],
          activities: Array.isArray(rawWishlist)
            ? rawWishlist.filter(({ type }) => type === 'thing_to_do')
            : [],
          accommodations: Array.isArray(rawWishlist)
            ? rawWishlist.filter(({ type }) => type === 'accommodation')
            : [],
        };

        // console.log(`Wishlist: ${JSON.stringify(wishlist)}`);

        setWishlist(wishlist);

        setIntro(intro);

        setForm(form);

        setWaiting(false);
      } catch (error) {
        console.error("Can't fetch wishlist", error);
      }
    })();
  }, []);

  const determineFormBeingPosted = () => {
    if (showShareWithAFriend) return 'wishlist-friend';
    if (showShareWithUs) return 'wishlist';
  };

  const handleReCaptchaVerify = useCallback(
    async (formAction) => {
      if (!executeRecaptcha) {
        console.warn('Recaptcha not ready yet.');
        return null;
      }

      console.log(`Check form action: ${formAction}`);
      const token = await executeRecaptcha(formAction);

      return token;
    },
    [executeRecaptcha]
  );

  const postForm = async (data, recaptchaToken, callback) => {
    if (!recaptchaToken || recaptchaToken.trim().length < 1) {
      alert('Please fill out the Recaptcha');
    } else {
      // Waiting is handled in the UI component for Share rather than in Hook in Contact
      // if we can't make it work in UI component, we can move this back here and pass it as a prop
      // setWaiting(true);

      try {
        const extraFields = {
          form_reference: determineFormBeingPosted(),
          id: `${determineFormBeingPosted()}-form`,
        };

        data = {
          ...data,
          favourites: flattenedFavourites.reduce(
            (previous, current, index) =>
              `${previous}${current}${
                index + 1 < flattenedFavourites.length ? ',' : ''
              }`,
            ''
          ),
        };

        await postFormData({ ...data, ...extraFields }, recaptchaToken);

        if (callback) callback();
      } catch (error) {
        console.error("Can't post form data", error);

        if (error.response?.data?.message?.includes('recaptcha')) {
          alert('Invalid recaptcha response');
        }
      }

      // Waiting is handled in the UI component for Share rather than in Hook in Contact
      // if we can't make it work in UI component, we can move this back here and pass it as a prop
      // setWaiting(false);
    }
  };

  // Rich: This is non-functional as initialValues doesn't exist in this component,
  // the initial state is set in shareWithUsInitial and shareWithAFriendInitial.
  // Doesn't seem to cause issues while form works but we should monitor.
  const disable = (errors, values, initialValues) => {
    const hasErrors = Object.keys(errors).length > 0;

    return (
      hasErrors || JSON.stringify(values) === JSON.stringify(initialValues)
    );
  };

  return {
    waiting,
    setWaiting,
    wishlist,
    showShareWithAFriend,
    setShowShareWithAFriend,
    showShareWithUs,
    setShowShareWithUs,
    shareWithAFriendInitial,
    shareWithAFriendValidationSchema,
    shareWithUsInitial,
    shareWithUsValidationSchema,
    currentFormAction,
    setCurrentFormAction,
    form,
    intro,
    disable,
    postForm,
    flattenedFavourites,
    handleReCaptchaVerify,
  };
};

export default useWishList;
